import { useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "react-query";
import { useGetAllPuckPages, getAllPuckPagesQueryKey } from "src/api/client-api/cms/useGetAllPuckPages";
import { PuckPage } from "src/api/client-api/cms/useCreatePuckPage";
import { useCMSPagesActions, DuplicatePayload } from "./useCMSPagesActions";
import { useCMSPagesTableState } from "./useCMSPagesTableState";

export type CreatePDFTemplatePayload = {
    title: string;
    description?: string;
    downloadOnlyOnSsqValidated?: boolean;
    allowedRoles: string[];
    url?: string;
    sectors?: string[];
    score?: string[];
    entity?: string[];
};

export type UpdatePDFTemplateMetaPayload = {
    url?: string;
    sectors?: string[] | null;
    allowedRoles?: string[];
    status?: "active" | "draft";
    title?: string;
    description?: string;
    downloadOnlyOnSsqValidated?: boolean;
    score?: string | string[];
    entity?: string | string[];
};

export const useCMSTemplatesManagerController = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
    const [pageToDelete, setPageToDelete] = useState<string | null>(null);

    const PAGE_SIZE = 20;
    const table = useCMSPagesTableState(PAGE_SIZE);

    const {
        pages = [],
        totalItems,
        isLoading,
        isError,
        refetch,
    } = useGetAllPuckPages({
        page: table.pagination.pageIndex + 1,
        itemsPerPage: table.pagination.pageSize,
        search: table.filters.title,
        pageType: "PDF",
        orderBy: Object.keys(table.orderBy).length > 0 ? table.orderBy : { createdAt: "desc" },
        url: table.filters.url || undefined,
        sectorId: table.filters.sectorId,
        status: table.filters.status,
    });

    const actions = useCMSPagesActions(pages, refetch, "PDF");

    const navigateToEditor = (slug: string, isNewlyCreated = false) => {
        const newFlag = isNewlyCreated ? "&new=true" : "";
        navigate(`/settings/cms/${slug}?t=${Date.now()}${newFlag}&from=templates`);
    };

    const getEditUrl = (slug: string) => `/settings/cms/${slug}?from=templates`;

    const handleDuplicateTemplate = async (source: PuckPage, payload: DuplicatePayload) => {
        try {
            setIsCreating(true);
            const newPage = await actions.duplicatePage(source, payload);
            queryClient.invalidateQueries(["puckPage", newPage.slug]);
            queryClient.invalidateQueries([getAllPuckPagesQueryKey]);
            await refetch();
        } catch (e) {
            console.error("Duplicate page failed", e);
        } finally {
            setIsCreating(false);
        }
    };

    const handleUpdateMeta = async (slug: string, payload: UpdatePDFTemplateMetaPayload) => {
        try {
            return await actions.updateMeta(slug, {
                title: payload.title,
                description: payload.description,
                downloadOnlyOnSsqValidated: payload.downloadOnlyOnSsqValidated,
                allowedRoles: payload.allowedRoles,
                status: payload.status,
                url: payload.url,
                sectors: payload.sectors ?? undefined,
                score: payload.score,
                entity: payload.entity,
            });
        } catch (error) {
            return console.error("Error updating page meta:", error);
        }
    };

    const handleCreateTemplate = async (payload: CreatePDFTemplatePayload) => {
        setIsCreating(true);

        const defaultPdfSettings = {
            format: "A3" as const,
            landscape: "portrait" as const,
            marginsMm: {
                top: 12,
                right: 12,
                bottom: 12,
                left: 12,
            },
        };

        const initialContent = {
            content: [],
            root: {
                props: {
                    title: payload.title,
                    description: payload.description ?? "",
                    downloadOnlyOnSsqValidated: payload.downloadOnlyOnSsqValidated ?? true,
                    pageType: "PDF",
                    score: payload.score ?? [],
                    entity: payload.entity ?? [],
                    pdfSettings: defaultPdfSettings,
                },
            },
            roles: {
                allowedRoles: payload.allowedRoles,
            },
        };

        try {
            const result = await actions.createPage({
                title: payload.title,
                content: JSON.stringify(initialContent),
                allowedRoles: payload.allowedRoles,
                url: payload.url,
                sectors: payload.sectors,
                status: "draft",
                pageType: "PDF",
            });
            queryClient.invalidateQueries([getAllPuckPagesQueryKey]);
            queryClient.invalidateQueries(["puckPage", result.slug]);
            queryClient.refetchQueries([getAllPuckPagesQueryKey]);
            await refetch();
        } catch (error) {
            console.error("Error creating page:", error);
        } finally {
            setIsCreateDialogOpen(false);
            setIsCreating(false);
        }
    };

    const confirmDelete = (slug: string) => {
        setPageToDelete(slug);
        setDeleteConfirmDialogOpen(true);
    };

    const closeDeleteConfirmDialog = () => {
        setDeleteConfirmDialogOpen(false);
        setPageToDelete(null);
    };

    const handleDeleteTemplate = () => {
        if (!pageToDelete) return;

        actions
            .deletePage(pageToDelete)
            .then(async () => {
                setDeleteConfirmDialogOpen(false);
                setPageToDelete(null);
            })
            .catch(() => {});
    };

    const handleUpdateTitle = async (slug: string, title: string) => {
        try {
            await actions.updateTitle(slug, title);
        } catch (error) {
            console.error("Failed to update title:", error);
        }
    };

    const handleUpdateStatus = (slug: string, status: "active" | "draft") => {
        actions.updateStatus(slug, status).catch((error) => console.error("Error updating status:", error));
    };

    const handleUpdatePageRoles = (slug: string, allowedRoles: string[]) => {
        actions.updateRoles(slug, allowedRoles).catch((error) => console.error("Error updating page roles:", error));
    };

    return {
        pages,
        totalItems,
        isLoading,
        isError,
        table,
        actions,
        isCreating,
        isCreateDialogOpen,
        openCreateDialog: () => setIsCreateDialogOpen(true),
        closeCreateDialog: () => setIsCreateDialogOpen(false),
        createTemplate: handleCreateTemplate,
        updateMeta: handleUpdateMeta,
        updateTitle: handleUpdateTitle,
        updateStatus: handleUpdateStatus,
        updateRoles: handleUpdatePageRoles,
        duplicateTemplate: handleDuplicateTemplate,
        navigateToEditor,
        getEditUrl,
        confirmDelete,
        deleteConfirmDialogOpen,
        closeDeleteConfirmDialog,
        deleteTemplate: handleDeleteTemplate,
        pageToDelete,
    };
};
